import {memo, useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {MAX_TITLE_LENGTH} from '../validation';
import {DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {checklistShape} from '../shapes';
import ChecklistItem from './ChecklistItem';
import useClickOutside from '../hooks/useClickOutside';
import useMenuPosition from '../hooks/useMenuPosition';

function SortableItem({
    item,
    checklistId,
    onToggle,
    onDelete,
    onUpdate,
    onStar,
    onSetDueDate,
    shareEditMode,
    readOnly,
}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id});
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="checklist-item-wrapper" {...listeners} {...attributes}>
            <ChecklistItem
                checklistId={checklistId}
                item={item}
                onToggle={onToggle}
                onDelete={onDelete}
                onUpdate={onUpdate}
                onStar={onStar}
                onSetDueDate={onSetDueDate}
                shareEditMode={shareEditMode}
                readOnly={readOnly}
            />
        </div>
    );
}

const Checklist = memo(function Checklist({
    data,
    onDelete,
    onAddItem,
    onToggleItem,
    onDeleteItem,
    onUpdateTitle,
    onUpdateItem,
    onReorderItems,
    onToggleSortMode,
    onToggleSinkChecked,
    onToggleHideChecked,
    onStarItem,
    onSetDueDateItem,
    readOnly,
    shareEditMode,
    isFocused,
    onFocus,
    onUnfocus,
    dragListeners,
    isDragEnabled,
    onShare,
    hasShares,
}) {
    const [newItemTitle, setNewItemTitle] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [completedExpanded, setCompletedExpanded] = useState(false);
    const menuRef = useRef(null);
    const menuBtnRef = useRef(null);
    const {menuOpen, dropdownPos, toggleMenu: openMenu, setMenuOpen} = useMenuPosition(menuBtnRef);
    const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
    useClickOutside(menuOpen, closeMenu, menuRef, menuBtnRef);

    const handleFocusToggle = useCallback(
        () => (isFocused ? onUnfocus() : onFocus(data.id)),
        [isFocused, onUnfocus, onFocus, data.id],
    );
    const handleSinkChecked = useCallback(() => onToggleSinkChecked(data.id), [onToggleSinkChecked, data.id]);
    const handleHideChecked = useCallback(() => onToggleHideChecked(data.id), [onToggleHideChecked, data.id]);
    const handleMenuSinkChecked = useCallback(() => {
        onToggleSinkChecked(data.id);
        setMenuOpen(false);
    }, [onToggleSinkChecked, data.id, setMenuOpen]);
    const handleMenuHideChecked = useCallback(() => {
        onToggleHideChecked(data.id);
        setMenuOpen(false);
    }, [onToggleHideChecked, data.id, setMenuOpen]);
    const handleMenuDelete = useCallback(() => {
        onDelete(data.id);
        setMenuOpen(false);
    }, [onDelete, data.id, setMenuOpen]);

    const isCustomSort = data.sortMode === 'custom';

    const displayedItems = useMemo(() => {
        let items = isCustomSort ? data.items : [...data.items].sort((a, b) => a.position - b.position);
        if (data.hideCheckedItems) {
            return items.filter(i => !i.checked);
        }
        if (data.sinkCheckedItems !== false) {
            const unchecked = items.filter(i => !i.checked);
            const checked = items.filter(i => i.checked);
            items = [...unchecked, ...checked];
        }
        return items;
    }, [data.items, isCustomSort, data.sinkCheckedItems, data.hideCheckedItems]);

    const checkedItems = useMemo(() => {
        if (!data.hideCheckedItems) return [];
        let items = isCustomSort ? data.items : [...data.items].sort((a, b) => a.position - b.position);
        return items.filter(i => i.checked);
    }, [data.items, isCustomSort, data.hideCheckedItems]);

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = displayedItems.findIndex(i => i.id === active.id);
        const newIndex = displayedItems.findIndex(i => i.id === over.id);
        const newItems = arrayMove(displayedItems, oldIndex, newIndex);
        const afterId = newIndex > 0 ? newItems[newIndex - 1].id : null;
        onReorderItems(data.id, active.id, afterId, newItems);
    }

    function handleAdd() {
        const title = newItemTitle.trim();
        if (!title) return;
        onAddItem(data.id, title);
        setNewItemTitle('');
    }

    function startEditingTitle() {
        setTitleDraft(data.title);
        setEditingTitle(true);
    }

    function commitTitle() {
        const title = titleDraft.trim();
        if (title && title !== data.title) onUpdateTitle(data.id, title);
        setEditingTitle(false);
    }

    function handleTitleKeyDown(e) {
        if (e.key === 'Enter') commitTitle();
        if (e.key === 'Escape') setEditingTitle(false);
    }

    const checked = data.items.filter(i => i.checked).length;
    const total = data.items.length;
    const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

    return (
        <div className={`checklist${isFocused ? ' checklist--focused' : ''}`}>
            <div className="checklist-sticky">
                <div className="checklist-header">
                    {isDragEnabled && (
                        <span
                            className="checklist-drag-handle"
                            {...(dragListeners || {})}
                            aria-label="Drag to reorder checklist"
                        >
                            <svg
                                width="10"
                                height="14"
                                viewBox="0 0 10 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="3" cy="2.5" r="1.2" fill="currentColor" />
                                <circle cx="7" cy="2.5" r="1.2" fill="currentColor" />
                                <circle cx="3" cy="7" r="1.2" fill="currentColor" />
                                <circle cx="7" cy="7" r="1.2" fill="currentColor" />
                                <circle cx="3" cy="11.5" r="1.2" fill="currentColor" />
                                <circle cx="7" cy="11.5" r="1.2" fill="currentColor" />
                            </svg>
                        </span>
                    )}
                    {editingTitle && !readOnly ? (
                        <input
                            className="checklist-title-input"
                            value={titleDraft}
                            maxLength={MAX_TITLE_LENGTH}
                            onChange={e => setTitleDraft(e.target.value)}
                            onBlur={commitTitle}
                            onKeyDown={handleTitleKeyDown}
                            autoFocus
                        />
                    ) : (
                        <h2
                            onClick={readOnly ? undefined : startEditingTitle}
                            onKeyDown={readOnly ? undefined : e => e.key === 'Enter' && startEditingTitle()}
                            tabIndex={readOnly ? undefined : 0}
                            className={!data.title ? 'checklist-title-empty' : undefined}
                        >
                            {data.title || 'Untitled'}
                        </h2>
                    )}
                    {!readOnly && !shareEditMode && (
                        <div className="checklist-header-actions">
                            {onShare && (
                                <button
                                    className={`checklist-share-btn${hasShares ? ' checklist-share-btn--active' : ''}`}
                                    onClick={() => onShare(data.id)}
                                    aria-label="Share checklist"
                                    title="Share"
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="9" cy="2.5" r="1.8" stroke="currentColor" strokeWidth="1.2" />
                                        <circle cx="3" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.2" />
                                        <circle cx="9" cy="9.5" r="1.8" stroke="currentColor" strokeWidth="1.2" />
                                        <line
                                            x1="4.5"
                                            y1="5"
                                            x2="7.5"
                                            y2="3.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <line
                                            x1="4.5"
                                            y1="7"
                                            x2="7.5"
                                            y2="8.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                    </svg>
                                </button>
                            )}
                            <button
                                className="checklist-focus-btn"
                                onClick={handleFocusToggle}
                                aria-label={isFocused ? 'Exit focus mode' : 'Focus checklist'}
                            >
                                {isFocused ? (
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <polyline
                                            points="4,1 1,1 1,4"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="8,1 11,1 11,4"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="11,8 11,11 8,11"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="4,11 1,11 1,8"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <polyline
                                            points="1,4 1,1 4,1"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="11,4 11,1 8,1"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="8,11 11,11 11,8"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <polyline
                                            points="1,8 1,11 4,11"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </button>
                            <button
                                className={`checklist-sort-btn${data.sinkCheckedItems !== false ? ' checklist-sort-btn--active' : ''}`}
                                onClick={handleSinkChecked}
                                aria-label="Checked on bottom"
                                title="Checked on bottom"
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M2.5 1.5L5 4L7.5 1.5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <line
                                        x1="5"
                                        y1="4"
                                        x2="5"
                                        y2="10.5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M8.5 7L10 8.5L11.5 7"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <button
                                className={`checklist-hide-btn${data.hideCheckedItems ? ' checklist-hide-btn--active' : ''}`}
                                onClick={handleHideChecked}
                                aria-label="Hide completed"
                                title="Hide completed"
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1 6C1 6 3 2 6 2C9 2 11 6 11 6C11 6 9 10 6 10C3 10 1 6 1 6Z"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <circle cx="6" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3" />
                                    <line
                                        x1="2"
                                        y1="10"
                                        x2="10"
                                        y2="2"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                            <button
                                ref={menuBtnRef}
                                className="checklist-header-menu-btn"
                                onClick={openMenu}
                                aria-label="Checklist settings"
                                aria-expanded={menuOpen}
                            >
                                <svg
                                    width="3"
                                    height="12"
                                    viewBox="0 0 3 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle cx="1.5" cy="2" r="1.4" fill="currentColor" />
                                    <circle cx="1.5" cy="6" r="1.4" fill="currentColor" />
                                    <circle cx="1.5" cy="10" r="1.4" fill="currentColor" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {total > 0 && (
                        <div className="checklist-header-progress">
                            <div className="checklist-header-progress-fill" style={{width: `${pct}%`}} />
                        </div>
                    )}
                </div>
                {!readOnly && !shareEditMode && (
                    <div className="checklist-add-item">
                        <div className="checklist-add-item-inner">
                            <input
                                type="text"
                                placeholder="Add new item"
                                value={newItemTitle}
                                maxLength={MAX_TITLE_LENGTH}
                                onChange={e => setNewItemTitle(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                            />
                            <button onClick={handleAdd}>Add</button>
                        </div>
                    </div>
                )}
            </div>
            <div className="checklist-body">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={displayedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                        {displayedItems.map(item => (
                            <SortableItem
                                key={item.id}
                                item={item}
                                checklistId={data.id}
                                onToggle={onToggleItem}
                                onDelete={onDeleteItem}
                                onUpdate={onUpdateItem}
                                onStar={onStarItem}
                                onSetDueDate={onSetDueDateItem}
                                shareEditMode={shareEditMode}
                                readOnly={readOnly}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
                {displayedItems.length === 0 && total === 0 && (
                    <div className="card-empty-hint">Add your first item above</div>
                )}
                <div className="checklist-ghost-lines" />
            </div>
            <div className="checklist-completed-section">
                <button
                    className={`checklist-completed-toggle${checked === 0 ? ' checklist-completed-toggle--empty' : ''}`}
                    onClick={checked > 0 && data.hideCheckedItems ? () => setCompletedExpanded(v => !v) : undefined}
                    aria-expanded={data.hideCheckedItems && checked > 0 ? completedExpanded : undefined}
                >
                    {data.hideCheckedItems && checked > 0 && (
                        <span className="checklist-completed-arrow">{completedExpanded ? '\u25BC' : '\u25B6'}</span>
                    )}
                    Completed ({checked}/{total})
                </button>
                {data.hideCheckedItems && completedExpanded && checkedItems.length > 0 && (
                    <div className="checklist-completed-items">
                        {checkedItems.map(item => (
                            <ChecklistItem
                                key={item.id}
                                checklistId={data.id}
                                item={item}
                                onToggle={onToggleItem}
                                onDelete={onDeleteItem}
                                onUpdate={onUpdateItem}
                                onStar={onStarItem}
                                onSetDueDate={onSetDueDateItem}
                                shareEditMode={shareEditMode}
                                readOnly={readOnly}
                            />
                        ))}
                    </div>
                )}
            </div>
            {menuOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="checklist-item-menu-dropdown"
                        role="menu"
                        style={{top: dropdownPos.top, right: dropdownPos.right}}
                    >
                        <button
                            className={`checklist-item-menu-item${data.sinkCheckedItems !== false ? ' checklist-item-menu-item--active' : ''}`}
                            onClick={handleMenuSinkChecked}
                            aria-label="Checked on bottom"
                            role="menuitem"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2.5 1.5L5 4L7.5 1.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <line
                                    x1="5"
                                    y1="4"
                                    x2="5"
                                    y2="10.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M8.5 7L10 8.5L11.5 7"
                                    stroke="currentColor"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Checked on bottom
                        </button>
                        <button
                            className={`checklist-item-menu-item${data.hideCheckedItems ? ' checklist-item-menu-item--active' : ''}`}
                            onClick={handleMenuHideChecked}
                            aria-label="Hide completed"
                            role="menuitem"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1 6C1 6 3 2 6 2C9 2 11 6 11 6C11 6 9 10 6 10C3 10 1 6 1 6Z"
                                    stroke="currentColor"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <circle cx="6" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3" />
                                <line
                                    x1="2"
                                    y1="10"
                                    x2="10"
                                    y2="2"
                                    stroke="currentColor"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                />
                            </svg>
                            Hide completed
                        </button>
                        <button
                            className="checklist-item-menu-item checklist-item-menu-item--danger"
                            onClick={handleMenuDelete}
                            aria-label="Delete checklist"
                            role="menuitem"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <polyline
                                    points="1,3 11,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Delete
                        </button>
                    </div>,
                    document.querySelector('.App') || document.body,
                )}
        </div>
    );
});

Checklist.propTypes = {
    data: checklistShape.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAddItem: PropTypes.func.isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onUpdateTitle: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
    onReorderItems: PropTypes.func.isRequired,
    onToggleSortMode: PropTypes.func.isRequired,
    onToggleSinkChecked: PropTypes.func.isRequired,
    onToggleHideChecked: PropTypes.func.isRequired,
    onStarItem: PropTypes.func.isRequired,
    onSetDueDateItem: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    shareEditMode: PropTypes.bool,
    isFocused: PropTypes.bool,
    onFocus: PropTypes.func.isRequired,
    onUnfocus: PropTypes.func.isRequired,
    dragListeners: PropTypes.object,
    isDragEnabled: PropTypes.bool,
    onShare: PropTypes.func,
    hasShares: PropTypes.bool,
};

export default Checklist;
